import type { BannerKind, PlatformId } from './types.js';

/** Gängige Werbeformate mit den im deutschsprachigen Raum üblichen Namen. */
const FORMAT_NAMES: Record<string, string> = {
  '728x90': 'Superbanner',
  '970x90': 'Large Leaderboard',
  '468x60': 'Fullsize Banner',
  '300x250': 'Medium Rectangle',
  '336x280': 'Large Rectangle',
  '160x600': 'Wide Skyscraper',
  '120x600': 'Skyscraper',
  '300x600': 'Halfpage Ad',
  '300x1050': 'Portrait',
  '970x250': 'Billboard',
  '800x250': 'Billboard',
  '250x250': 'Square',
  '200x200': 'Small Square',
  '320x50': 'Mobile Leaderboard',
  '300x50': 'Mobile Banner',
  '320x100': 'Large Mobile Banner',
  '320x480': 'Mobile Interstitial',
  '1080x1080': 'Social Square 1:1',
  '1080x1350': 'Social Portrait 4:5',
  '1080x1920': 'Story / Reel 9:16',
  '1920x1080': 'Video 16:9',
  '1280x720': 'Video 16:9',
  '1200x628': 'Link-Anzeige 1,91:1',
  '1200x1200': 'Social Square 1:1',
};

export function formatName(width: number | null | undefined, height: number | null | undefined): string {
  if (!width || !height) return 'Ohne Format';
  return FORMAT_NAMES[`${width}x${height}`] ?? aspectName(width, height);
}

export function formatKey(width: number | null | undefined, height: number | null | undefined): string {
  return width && height ? `${width}x${height}` : 'unknown';
}

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

export function aspectRatio(width: number, height: number): string {
  const d = gcd(width, height);
  const w = width / d;
  const h = height / d;
  if (w > 40 || h > 40) return (width / height).toFixed(2).replace('.', ',') + ':1';
  return `${w}:${h}`;
}

function aspectName(width: number, height: number): string {
  const ratio = width / height;
  if (ratio > 2.5) return 'Querformat';
  if (ratio < 0.4) return 'Hochformat';
  return `Format ${aspectRatio(width, height)}`;
}

export const KIND_LABELS: Record<BannerKind, string> = {
  html5: 'HTML5',
  gif: 'GIF',
  image: 'Bild',
  video: 'Video',
};

export const PLATFORMS: { id: PlatformId; name: string; kinds: BannerKind[] }[] = [
  { id: 'google_ads', name: 'Google Ads', kinds: ['html5', 'image', 'gif'] },
  { id: 'cm360', name: 'Campaign Manager 360 / DV360', kinds: ['html5'] },
  { id: 'adform', name: 'Adform', kinds: ['html5'] },
  { id: 'amazon_dsp', name: 'Amazon DSP', kinds: ['html5'] },
  { id: 'ttd', name: 'The Trade Desk', kinds: ['html5'] },
  { id: 'meta', name: 'Meta (Facebook/Instagram)', kinds: ['image', 'gif', 'video'] },
];

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '–';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0).replace('.', ',')} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
}

export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '–';
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0).replace('.', ',')} s`;
  const m = Math.floor(s / 60);
  const rest = Math.round(s % 60);
  return `${m}:${String(rest).padStart(2, '0')} min`;
}
